/**
 * Homeowner-saved providers.
 * Settlement trades stay on the homeowner's private list.
 * Agent payloads drop notes and those trades before they leave the server.
 */

const { normalizeCategory } = require('./vendorCategories');
const { vendorCategoryOptions, vendorMatchesTrade, vendorsMatchingTrade } = require('./vendorTrades');

const SETTLEMENT_SERVICE_TRADES = [
  'Lender/Mortgage',
  'Title',
  'Insurance',
  'Home Warranty',
  'Inspection',
  'Appraisal',
];

const ADDED_BY_YOU = 'Added by you';
const ADD_VENDOR = 'Add vendor';
const ADD_MY_OWN_PRO = ADD_VENDOR;
const YOUR_VENDORS = 'Your vendors';
const SAVED_SECTION_TITLE = YOUR_VENDORS;
const YOUR_VENDORS_HELP = 'People you added';
const SAVED_SECTION_LINE = YOUR_VENDORS_HELP;
const SAVED_VETTING_LINE =
  "Pacarion doesn't vet or guarantee providers. Check licenses and insurance before hiring.";
const SPONSOR_VENDORS_HELP = 'Shared by your sponsor';
const AGENT_VENDORS_FALLBACK = "Your sponsor's vendors";
const AGENT_YOUR_VENDORS_HELP = 'Vendors you added. Your homeowners see these.';
const TEAM_VENDORS = 'Team vendors';
const TEAM_VENDORS_HELP = 'Shared with your team and their homeowners.';
const EMPTY_ADD_VENDOR = 'No vendors yet. Tap Add vendor.';
const EMPTY_LIST_VENDORS = 'No vendors yet.';
const EMPTY_TEAM_VENDORS = 'No team vendors yet.';
const SHARE_NOTICE =
  "Your sponsor (and their team, if you're a team client) can see the name, trade, and contact info of pros you add, and may add them to their own provider list. Your notes stay private.";
const ADOPT_NOTICE =
  "Added by your client. Pacarion hasn't reviewed this provider. You're choosing to recommend them.";
const ADOPT_BUTTON = 'Add to your vendors';
const IN_YOUR_VENDORS = 'In your vendors';
const ADDED_BY_CLIENTS = 'From your homeowners';
const ADDED_BY_CLIENTS_LINE = 'Vendors they added. Add one to your vendors if you want.';
const ADDED_BY_CLIENTS_VALUE = 'added-by-clients';
const CLIENT_PROS_HEADING = 'Providers this client added';

const AGENT_PRO_COLUMNS = 'id, client_id, name, trade, phone, email, website, service_county_fips, service_statewide, created_at, updated_at';

const SETTLEMENT_KEYS = new Set(SETTLEMENT_SERVICE_TRADES.map((trade) => normalizeCategory(trade)));

function isSettlementServiceTrade(trade) {
  return SETTLEMENT_KEYS.has(normalizeCategory(trade));
}

function homeownerTradeOptions(custom) {
  return vendorCategoryOptions(custom).filter((name) => !isSettlementServiceTrade(name));
}

function canonicalTrade(trade, custom) {
  const key = normalizeCategory(trade);
  return homeownerTradeOptions(custom).find((option) => normalizeCategory(option) === key) || '';
}

function clean(value, max) {
  return String(value || '').trim().slice(0, max);
}

function normalizeWebsite(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, '')}`;
}

function proInput(record, custom) {
  const source = record || {};
  const name = clean(source.name, 200);
  const trade = canonicalTrade(source.trade, custom);
  if (!name || name.length > 120) return { error: 'Name is required' };
  if (!trade) return { error: 'Choose a trade' };
  const phone = clean(source.phone, 80);
  const email = clean(source.email, 200);
  const website = normalizeWebsite(clean(source.website, 300));
  const notes = clean(source.notes, 4000);
  if (phone.length > 40) return { error: 'Phone is too long' };
  if (email.length > 120) return { error: 'Email is too long' };
  if (website.length > 200) return { error: 'Website is too long' };
  if (notes.length > 2000) return { error: 'Notes are too long' };
  return {
    record: { name, trade, phone, email, website, notes },
  };
}

function agentVisiblePro(pro) {
  if (!pro || isSettlementServiceTrade(pro.trade)) return null;
  const name = String(pro.name || '').trim();
  const trade = canonicalTrade(pro.trade) || String(pro.trade || '').trim();
  if (!name || !trade || isSettlementServiceTrade(trade)) return null;
  return {
    id: pro.id,
    client_id: pro.client_id || null,
    name,
    trade,
    phone: pro.phone || '',
    email: pro.email || '',
    website: pro.website || '',
    service_county_fips: Array.isArray(pro.service_county_fips) ? pro.service_county_fips : [],
    service_statewide: Array.isArray(pro.service_statewide) ? pro.service_statewide : [],
    created_at: pro.created_at || null,
    updated_at: pro.updated_at || null,
  };
}

function shapeProsForAgent(rows) {
  return (rows || []).map(agentVisiblePro).filter(Boolean);
}

/**
 * Mirrors homeowner_pros_agent_select.
 * Only the client's owning agent (clients.agent_id) may read the pro.
 * Settlement trades are never visible. Another team, a team owner who does
 * not own the client, and an assigned agent who is not agent_id see nothing.
 */
function agentMayReadHomeownerPro({ pro, client, caller }) {
  if (!pro || !client || !caller) return false;
  if (isSettlementServiceTrade(pro.trade)) return false;
  if (pro.client_id && client.id && String(pro.client_id) !== String(client.id)) return false;
  const profileId = caller.profileId || caller.id;
  if (!profileId) return false;
  return client.agent_id === profileId;
}

function visibleProsForCaller(rows, clientsById, caller) {
  return (rows || []).filter((pro) => {
    const client = clientsById && (clientsById[pro.client_id] || clientsById[pro.clientId]);
    return agentMayReadHomeownerPro({ pro, client, caller });
  }).map(agentVisiblePro).filter(Boolean);
}

function prosMatchingTrade(pros, trade) {
  return (pros || []).filter((pro) => {
    if (!pro || isSettlementServiceTrade(pro.trade)) return false;
    return vendorMatchesTrade({ category: pro.trade }, trade);
  });
}

function withoutAdoptedCopies(agentVendors, homeownerPros) {
  const ownIds = new Set(
    (homeownerPros || []).map((pro) => pro && pro.id).filter(Boolean)
  );
  return (agentVendors || []).filter((vendor) => {
    const adoptedId = vendor && vendor.adopted_from_homeowner_pro_id;
    return !adoptedId || !ownIds.has(adoptedId);
  });
}

function findAProResults({ agentVendors, homeownerPros, trade }) {
  const visibleAgents = withoutAdoptedCopies(agentVendors, homeownerPros);
  const savedPros = prosMatchingTrade(homeownerPros, trade);
  const matchedAgents = vendorsMatchingTrade(visibleAgents, trade);
  if (savedPros.length === 0 && matchedAgents.length === 0) {
    return {
      tradeMiss: true,
      agentVendors: visibleAgents,
      savedPros: [],
    };
  }
  return {
    tradeMiss: false,
    agentVendors: matchedAgents,
    savedPros,
  };
}

function alreadyAdopted(proId, vendors) {
  if (!proId) return false;
  return (vendors || []).some((vendor) => vendor && vendor.adopted_from_homeowner_pro_id === proId);
}

function adoptVendorPayload(pro, profile, access) {
  const visible = agentVisiblePro(pro);
  if (!visible || !profile || !profile.id) return null;
  if (access && !agentMayReadHomeownerPro({
    pro,
    client: access.client,
    caller: access.caller || profile,
  })) return null;
  const visibility = profile.visibility === 'team' && profile.team_id ? 'team' : 'personal';
  const row = {
    agent_id: profile.id,
    created_by: profile.id,
    name: visible.name,
    category: visible.trade,
    categories: [visible.trade],
    type: 'maintenance',
    phone: visible.phone || '',
    email: visible.email || '',
    website: normalizeWebsite(visible.website),
    discount: '',
    code: '',
    description: '',
    visibility,
    adopted_from_homeowner_pro_id: visible.id,
  };
  if (visibility === 'team') row.team_id = profile.team_id;
  return row;
}

function cleanLabel(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

/**
 * Homeowner section title for the sponsor's list.
 * Uses the sponsor's display name, then the team or brokerage name.
 * With neither, the section is "Your sponsor's vendors".
 */
function sponsorVendorHeading({ agentName, subject, orgName } = {}) {
  const given = cleanLabel(subject);
  if (given) return `From ${given}`;
  const name = cleanLabel(agentName);
  const lowered = name.toLowerCase();
  if (name && lowered !== 'your agent' && lowered !== 'your sponsor') return `From ${name}`;
  const org = cleanLabel(orgName);
  if (org) return `From ${org}`;
  return AGENT_VENDORS_FALLBACK;
}

function aiProPayload(pro) {
  const visible = agentVisiblePro(pro);
  if (!visible) return null;
  return {
    name: visible.name,
    category: visible.trade,
    phone: visible.phone || '',
    client_id: visible.client_id,
    listSource: 'saved',
  };
}

module.exports = {
  SETTLEMENT_SERVICE_TRADES,
  ADDED_BY_YOU,
  ADD_VENDOR,
  ADD_MY_OWN_PRO,
  YOUR_VENDORS,
  YOUR_VENDORS_HELP,
  SAVED_SECTION_TITLE,
  SAVED_SECTION_LINE,
  SAVED_VETTING_LINE,
  SPONSOR_VENDORS_HELP,
  AGENT_VENDORS_FALLBACK,
  AGENT_YOUR_VENDORS_HELP,
  TEAM_VENDORS,
  TEAM_VENDORS_HELP,
  EMPTY_ADD_VENDOR,
  EMPTY_LIST_VENDORS,
  EMPTY_TEAM_VENDORS,
  SHARE_NOTICE,
  ADOPT_NOTICE,
  ADOPT_BUTTON,
  IN_YOUR_VENDORS,
  ADDED_BY_CLIENTS,
  ADDED_BY_CLIENTS_LINE,
  ADDED_BY_CLIENTS_VALUE,
  normalizeWebsite,
  CLIENT_PROS_HEADING,
  AGENT_PRO_COLUMNS,
  isSettlementServiceTrade,
  homeownerTradeOptions,
  proInput,
  agentVisiblePro,
  shapeProsForAgent,
  agentMayReadHomeownerPro,
  visibleProsForCaller,
  prosMatchingTrade,
  withoutAdoptedCopies,
  findAProResults,
  alreadyAdopted,
  adoptVendorPayload,
  aiProPayload,
  sponsorVendorHeading,
};
